import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useHOA } from '../../contexts/HOAContext';
import { supabase } from '../../lib/supabase';
import { withSupabaseQueryTimeout } from '../../lib/supabaseTimeout';
import { logEvent } from '../../lib/activityLogger';
import {
  sendInvitation,
  shouldResetExistingForInvite,
} from '../../services/invitationService';
import { revokePortalUser } from '../../services/userDeletionService';
import { getPeopleRoster } from '../../services/activityService';
import { ConfirmDialog } from '../ui';
import { useModalDismiss } from '../ui/useModalDismiss';
import CreateUserModal from './CreateUserModal';
import RosterImportModal from './RosterImportModal';

const STATUS_STYLES = {
  no_email: 'bg-slate-100 text-slate-600 border border-slate-200',
  not_invited: 'bg-amber-50 text-amber-800 border border-amber-200',
  invitation_sent: 'bg-blue-50 text-blue-800 border border-blue-200',
  activated: 'bg-emerald-50 text-emerald-800 border border-emerald-200',
  attention: 'bg-rose-50 text-rose-800 border border-rose-200',
};

const STATUS_LABELS = {
  no_email: 'No email',
  not_invited: 'Not invited',
  invitation_sent: 'Invited',
  activated: 'Activated',
  attention: 'Needs review',
};

const ROLE_STYLES = {
  admin: 'bg-primary-dark/10 text-primary-dark border border-primary-dark/20',
  management: 'bg-secondary/10 text-secondary border border-secondary/20',
  board: 'bg-blue-50 text-blue-800 border border-blue-200',
  member: 'bg-stone-100 text-stone-700 border border-stone-300',
};

const ROLE_LABELS = {
  admin: 'Administrator',
  management: 'Management',
  board: 'Board Member',
  member: 'Member',
};

const ROLE_OPTIONS = [
  { value: 'member', label: 'Member (Homeowner)' },
  { value: 'board', label: 'Board Member' },
  { value: 'management', label: 'Property Management' },
  { value: 'admin', label: 'Administrator' },
];

const inputClass =
  'w-full px-4 py-3 bg-white border border-amber-100/30 hover:border-secondary/40 focus:border-secondary focus:ring-2 focus:ring-secondary/10 rounded-xl text-primary-dark placeholder-slate-400 text-sm font-medium outline-none transition-all';

const getSortNumber = (value) => {
  const parsed = parseInt(String(value || '').replace(/[^\d]/g, ''), 10);
  return Number.isFinite(parsed) ? parsed : 9999;
};

const getInitials = (nameOrEmail) => {
  const source = (nameOrEmail || '').trim();
  if (!source) return '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const formatBlockLot = (block, lot) => {
  if (!block && !lot) return '-';
  return `${block || '-'}/${lot || '-'}`;
};

export const formatLastLogin = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  });
};

export const isLastLoginStale = (value, now = new Date()) => {
  if (!value) return false;
  const loginDate = new Date(value);
  if (Number.isNaN(loginDate.getTime())) return false;

  const cutoff = new Date(now);
  cutoff.setMonth(cutoff.getMonth() - 3);
  return loginDate < cutoff;
};

const LastLoginValue = ({ value }) => {
  const formatted = formatLastLogin(value);
  if (!formatted) return null;

  return (
    <span className={`text-xs font-bold whitespace-nowrap ${isLastLoginStale(value) ? 'text-red-700' : 'text-primary-dark'}`}>
      {formatted}
    </span>
  );
};

const normalizeStatus = (invitation, profile) => {
  if (!invitation) return 'no_email';
  if (invitation.status === 'activated' && !invitation.auth_user_id) {
    console.warn(
      `Activated invitation ${invitation.id} has no auth_user_id; displaying as invited.`
    );
    return 'invitation_sent';
  }
  if (invitation.auth_user_id && !profile) {
    console.warn(
      `Invitation ${invitation.id} has auth_user_id but no profile row; displaying as invited.`
    );
    return invitation.status === 'activated' ? 'invitation_sent' : invitation.status;
  }
  if (!invitation.email && invitation.status !== 'activated') return 'no_email';
  return STATUS_LABELS[invitation.status] ? invitation.status : 'no_email';
};

const sortProperties = (a, b) => {
  const blockDiff = getSortNumber(a.block) - getSortNumber(b.block);
  if (blockDiff !== 0) return blockDiff;
  const lotDiff = getSortNumber(a.lot) - getSortNumber(b.lot);
  if (lotDiff !== 0) return lotDiff;
  return String(a.situs_address || '').localeCompare(String(b.situs_address || ''));
};

const sortInvitationRows = (a, b) => {
  const propertyDiff = sortProperties(a.property, b.property);
  if (propertyDiff !== 0) return propertyDiff;
  const createdA = a.invitation?.created_at ? new Date(a.invitation.created_at).getTime() : 0;
  const createdB = b.invitation?.created_at ? new Date(b.invitation.created_at).getTime() : 0;
  return createdA - createdB;
};

const getInviteEmail = (row) => row?.invitation_email || row?.roster_email || row?.email || '';
const normalizeInviteEmail = (value) =>
  String(value || '')
    .trim()
    .toLowerCase();

const normalizeRosterIdentityPart = (value) =>
  String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const getRosterClusterKey = (row) => {
  const ownerName = normalizeRosterIdentityPart(
    row?.invitation?.member_name || row?.member_name || row?.displayName
  );
  const block = normalizeRosterIdentityPart(row?.block);
  const lot = normalizeRosterIdentityPart(row?.lot);

  return ownerName && block && lot ? `${ownerName}|${block}|${lot}` : '';
};

export const groupMemberInvitationRows = (memberRows, occupantLinks = []) => {
  const linkedPropertiesByUser = new Map();
  occupantLinks.forEach((link) => {
    if (!link?.user_id || !link?.property_id || link.is_active === false) return;
    if (!linkedPropertiesByUser.has(link.user_id)) {
      linkedPropertiesByUser.set(link.user_id, new Set());
    }
    linkedPropertiesByUser.get(link.user_id).add(link.property_id);
  });

  const emailToUserId = new Map();
  memberRows.forEach((row) => {
    const email = normalizeInviteEmail(getInviteEmail(row));
    if (email && row.auth_user_id) emailToUserId.set(email, row.auth_user_id);
  });

  // Before an email or Auth account exists, a multi-unit owner can still be a
  // high-confidence roster match when the exact owner label and block/lot are
  // shared across distinct properties. Requiring one row per property prevents
  // this fallback from merging multiple people invited to the same address.
  const rosterClusters = new Map();
  memberRows.forEach((row) => {
    const clusterKey = getRosterClusterKey(row);
    if (!clusterKey) return;
    if (!rosterClusters.has(clusterKey)) rosterClusters.set(clusterKey, []);
    rosterClusters.get(clusterKey).push(row);
  });
  const eligibleRosterClusterKeys = new Set(
    [...rosterClusters.entries()]
      .filter(([, rows]) => {
        if (rows.length < 2) return false;
        if (
          rows.some(
            (row) => row.auth_user_id || normalizeInviteEmail(getInviteEmail(row))
          )
        ) {
          return false;
        }
        const propertyIds = rows.map((row) => row.property_id).filter(Boolean);
        return propertyIds.length === rows.length && new Set(propertyIds).size === rows.length;
      })
      .map(([clusterKey]) => clusterKey)
  );

  const groups = new Map();
  memberRows.forEach((row) => {
    const email = normalizeInviteEmail(getInviteEmail(row));
    const resolvedUserId = row.auth_user_id || emailToUserId.get(email) || null;
    const rosterClusterKey = getRosterClusterKey(row);
    const key = resolvedUserId
      ? `user:${resolvedUserId}`
      : email
        ? `email:${email}`
        : rosterClusterKey && eligibleRosterClusterKeys.has(rosterClusterKey)
          ? `roster:${rosterClusterKey}`
          : `invitation:${row.invitation_id || row.rowKey}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  });

  return [...groups.entries()].map(([accountKey, groupedRows]) => {
    const propertyRows = [...groupedRows].sort((a, b) => sortProperties(a, b));
    const lead = propertyRows[0];
    const profile = propertyRows.find((row) => row.profile)?.profile || null;
    const authUserId =
      propertyRows.find((row) => row.auth_user_id)?.auth_user_id || profile?.id || null;
    const linkedPropertyIds = linkedPropertiesByUser.get(authUserId) || new Set();
    const expectedPropertyIds = new Set(propertyRows.map((row) => row.property_id).filter(Boolean));
    const allExpectedPropertiesLinked = [...expectedPropertyIds].every((propertyId) =>
      linkedPropertyIds.has(propertyId)
    );
    const allInvitationsActivated = propertyRows.every((row) => row.status === 'activated');
    const hasActivatedInvitation = propertyRows.some((row) => row.status === 'activated');
    const hasSentInvitation = propertyRows.some((row) =>
      ['invitation_sent', 'opened'].includes(row.invitation?.status || row.status)
    );
    const email = getInviteEmail(lead);

    let status = email ? 'not_invited' : 'no_email';
    if (allInvitationsActivated && profile?.is_active !== false && allExpectedPropertiesLinked) {
      status = 'activated';
    } else if (hasActivatedInvitation || (authUserId && allInvitationsActivated)) {
      status = 'attention';
    } else if (hasSentInvitation || authUserId) {
      status = 'invitation_sent';
    }

    const profileName = profile?.full_name?.trim();
    const displayName =
      (['activated', 'attention'].includes(status) && profileName) ||
      lead.displayName ||
      lead.member_name ||
      email ||
      'Unnamed';

    return {
      ...lead,
      rowKey: `account-${accountKey}`,
      accountKey,
      auth_user_id: authUserId,
      profile,
      status,
      displayName,
      invitation_email: email,
      invitation_ids: propertyRows.map((row) => row.invitation_id).filter(Boolean),
      propertyRows,
      property_count: expectedPropertyIds.size,
      grouping_basis: accountKey.startsWith('roster:')
        ? 'owner_block_lot'
        : accountKey.startsWith('email:')
          ? 'email'
          : accountKey.startsWith('user:')
            ? 'auth_user'
            : 'single_invitation',
      linked_property_count: [...expectedPropertyIds].filter((propertyId) =>
        linkedPropertyIds.has(propertyId)
      ).length,
      linked_property_ids: [...linkedPropertyIds],
      has_link_mismatch: status === 'attention',
      is_acc_member: propertyRows.some((row) => row.is_acc_member === true),
      searchValues: propertyRows.flatMap((row) => [
        row.displayName,
        row.member_name,
        row.portal_full_name,
        row.situs_address,
        row.block,
        row.lot,
        row.invitation_email,
        row.roster_email,
      ]),
    };
  });
};

const getDeleteDisplayName = (row) =>
  row?.displayName || row?.portal_full_name || row?.full_name || getInviteEmail(row) || 'this user';
const getDeleteTargetUserId = (row) => row?.auth_user_id || row?.profile?.id || null;

const canSendInvite = (row) => {
  const email = getInviteEmail(row);
  return Boolean(email) && row.status !== 'no_email';
};

const MaterialIcon = ({ name, className = '', size = 20 }) => (
  <span className={`material-symbols-outlined ${className}`} style={{ fontSize: `${size}px` }}>
    {name}
  </span>
);

const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${STATUS_STYLES[status] || STATUS_STYLES.no_email}`}
  >
    {STATUS_LABELS[status] || STATUS_LABELS.no_email}
  </span>
);

const RoleBadge = ({ role }) => {
  if (!role) return <span className="text-slate-400 font-bold">-</span>;
  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${ROLE_STYLES[role] || ROLE_STYLES.member}`}
    >
      {ROLE_LABELS[role] || role}
    </span>
  );
};

const ACCBadge = ({ show }) =>
  show ? (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest bg-amber-50 text-amber-800 border border-amber-200">
      ACC
    </span>
  ) : null;

const StatCard = ({ icon, label, value, accent = 'text-primary-dark', testId }) => (
  <div
    data-testid={testId}
    className="bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30 p-6"
  >
    <div className="flex items-start justify-between gap-4">
      <div>
        <p
          data-testid={testId ? `${testId}-value` : undefined}
          className={`text-4xl font-extrabold tracking-tight leading-none ${accent}`}
        >
          {value}
        </p>
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mt-2">
          {label}
        </p>
      </div>
      <div className="w-12 h-12 bg-section-bg rounded-xl flex items-center justify-center">
        <MaterialIcon name={icon} className="text-secondary" size={24} />
      </div>
    </div>
  </div>
);

const SkeletonRows = ({ colSpan = 9 }) => (
  <>
    {[0, 1, 2, 3].map((row) => (
      <tr key={row} className="animate-pulse">
        <td colSpan={colSpan} className="px-6 py-5">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-section-bg" />
            <div className="space-y-2 flex-1">
              <div className="h-3 bg-section-bg rounded-full w-1/4" />
              <div className="h-3 bg-section-bg rounded-full w-1/2" />
            </div>
            <div className="h-8 bg-section-bg rounded-full w-28" />
          </div>
        </td>
      </tr>
    ))}
  </>
);

const EmptyState = ({ title, description, icon = 'person_search' }) => (
  <div className="flex flex-col items-center justify-center py-20 text-center bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30">
    <div className="w-16 h-16 bg-section-bg rounded-2xl flex items-center justify-center mb-6 warm-tactile-shadow">
      <MaterialIcon name={icon} className="text-secondary" size={36} />
    </div>
    <h3 className="text-xl font-bold text-primary-dark mb-2">{title}</h3>
    <p className="text-sm font-medium text-slate-500 max-w-sm leading-relaxed">{description}</p>
  </div>
);

const ActionMenu = ({ rowKey, openMenu, setOpenMenu, actions, disabled }) => {
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const isOpen = openMenu === rowKey;

  const updateMenuPosition = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      setMenuPosition(null);
      return;
    }

    const menuWidth = 192;
    const estimatedMenuHeight = actions.length * 44 + 16;
    const left = Math.min(window.innerWidth - menuWidth - 16, Math.max(16, rect.right - menuWidth));
    const belowTop = rect.bottom + 8;
    const top =
      belowTop + estimatedMenuHeight > window.innerHeight - 16
        ? Math.max(16, rect.top - estimatedMenuHeight - 8)
        : belowTop;

    setMenuPosition({ top, left, width: menuWidth });
  }, [actions.length]);

  useEffect(() => {
    if (!isOpen) return undefined;
    updateMenuPosition();

    const closeOnOutsideClick = (event) => {
      const clickedMenu = menuRef.current?.contains(event.target);
      const clickedButton = buttonRef.current?.contains(event.target);
      if (!clickedMenu && !clickedButton) {
        setOpenMenu(null);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setOpenMenu(null);
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isOpen, setOpenMenu, updateMenuPosition]);

  const menu = (
    <AnimatePresence>
      {isOpen && menuPosition && (
        <motion.div
          ref={menuRef}
          initial={{ opacity: 0, y: -4, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -4, scale: 0.98 }}
          style={menuPosition}
          className="fixed z-[70] bg-white border border-amber-100/40 rounded-2xl warm-tactile-shadow p-2"
        >
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              onClick={() => {
                setOpenMenu(null);
                action.onClick();
              }}
              disabled={disabled || action.disabled}
              className={`w-full flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                action.danger
                  ? 'text-red-700 hover:bg-red-50'
                  : 'text-primary-dark hover:bg-section-bg'
              }`}
            >
              <MaterialIcon name={action.icon} size={18} />
              {action.label}
            </button>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div ref={menuRef} className="relative flex justify-end">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpenMenu(isOpen ? null : rowKey)}
        className="w-10 h-10 rounded-full border border-amber-100/40 bg-white hover:bg-section-bg text-primary-dark flex items-center justify-center transition-all"
        aria-label="Open actions"
        aria-expanded={isOpen}
        disabled={disabled}
      >
        <MaterialIcon name="more_horiz" size={20} />
      </button>
      {typeof document !== 'undefined' ? createPortal(menu, document.body) : null}
    </div>
  );
};

const InviteConfirmModal = ({ invite, loading, error, onCancel, onConfirm }) => {
  useModalDismiss({ isOpen: Boolean(invite), onClose: onCancel });
  if (!invite) return null;
  const invites = invite.invites || [];
  const isBulk = invites.length > 1;
  const resetCount = invites.filter(({ row }) => shouldResetExistingForInvite(row)).length;
  const propertyCount = invites.reduce(
    (total, { row }) => total + Number(row.property_count || (row.property_id ? 1 : 0)),
    0
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={onCancel} role="presentation" data-modal-backdrop="true">
      <div className="relative bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30 p-6 max-w-md w-full" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="invite-confirm-title">
        <button type="button" onClick={onCancel} aria-label="Close invitation confirmation" className="absolute top-4 right-4 w-10 h-10 rounded-full hover:bg-section-bg flex items-center justify-center text-primary-dark">
          <MaterialIcon name="close" size={20} />
        </button>
        <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center mb-4">
          <MaterialIcon name="mail" className="text-amber-600" size={24} />
        </div>
        <h3 id="invite-confirm-title" className="text-lg font-bold text-primary-dark mb-2 pr-10">
          {isBulk ? `Send ${invites.length} invitation emails?` : 'Send invitation email?'}
        </h3>
        <p className="text-sm font-medium text-slate-500 leading-relaxed mb-4">
          {isBulk
            ? `Real emails will be sent to ${invites.length} people.${propertyCount > 0 ? ` Their accounts include ${propertyCount} property relationship${propertyCount === 1 ? '' : 's'}.` : ''}`
            : propertyCount > 0
              ? `A real email will be sent to ${invites[0]?.email || invite.email}. The account will cover ${propertyCount} propert${propertyCount === 1 ? 'y' : 'ies'}.`
              : `A real email will be sent to ${invites[0]?.email || invite.email}. The recipient will receive a portal sign-up link.`}
          {resetCount > 0
            ? ' Existing portal access will be reset first so the welcome email can be sent.'
            : ''}
        </p>
        {isBulk && (
          <div className="max-h-56 overflow-y-auto rounded-2xl border border-amber-100/40 bg-section-bg p-3 mb-6 space-y-2">
            {invites.map(({ row, email }) => (
              <div
                key={row.rowKey}
                className="bg-white rounded-xl border border-amber-100/30 px-3 py-2"
              >
                <p className="text-sm font-bold text-primary-dark">
                  {row.displayName || row.member_name || email}
                </p>
                <p className="text-xs font-medium text-slate-500 break-words">{email}</p>
                {row.property_count > 0 && (
                  <p className="text-[10px] font-bold uppercase tracking-widest text-secondary mt-1">
                    {row.property_count} propert{row.property_count === 1 ? 'y' : 'ies'}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
        {error && (
          <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700">
            {error}
          </div>
        )}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="flex-1 border border-slate-200 text-slate-700 px-4 py-2.5 rounded-full font-bold text-sm hover:bg-section-bg transition-all disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 bg-secondary text-white px-4 py-2.5 rounded-full font-bold text-sm hover:brightness-110 active:scale-95 transition-all disabled:opacity-50"
          >
            {loading
              ? `Sending${isBulk ? ` ${invites.length}` : ''}...`
              : isBulk
                ? 'Send invitations'
                : 'Send invitation'}
          </button>
        </div>
      </div>
    </div>
  );
};

const UserCell = ({ row }) => {
  const displayName = row.displayName || row.situs_address || 'Unnamed';
  const isUnnamed = !row.displayName;
  const isActivated = row.status === 'activated';
  return (
    <div className="flex items-center gap-3 min-w-64">
      <div
        className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 font-extrabold text-sm ${
          isActivated ? 'bg-amber-100 text-amber-900' : 'bg-slate-100 text-slate-500'
        }`}
      >
        {getInitials(displayName)}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-bold text-primary-dark truncate inline-flex items-center gap-2 flex-wrap">
          <span className="truncate">{displayName}</span>
          {isUnnamed && <span className="text-slate-400 font-medium">(no name yet)</span>}
        </p>
        <p className="text-xs font-medium truncate text-slate-500">
          {row.property_count > 1
            ? `${row.property_count} properties`
            : row.situs_address || 'No address'}
        </p>
      </div>
    </div>
  );
};

const PropertyRelationshipList = ({ row }) => {
  const linkedPropertyIds = new Set(row.linked_property_ids || []);
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
      {(row.propertyRows || []).map((propertyRow) => {
        const isLinked = linkedPropertyIds.has(propertyRow.property_id);
        const relationshipLabel = String(propertyRow.link_type || 'owner').replace(/_/g, ' ');
        return (
          <div
            key={propertyRow.property_id || propertyRow.rowKey}
            className="bg-white rounded-2xl border border-amber-100/40 p-4 flex items-start justify-between gap-4"
          >
            <div className="min-w-0">
              <p className="text-sm font-bold text-primary-dark break-words">
                {propertyRow.situs_address || 'Address unavailable'}
              </p>
              <p className="text-xs font-medium text-slate-500 mt-1">
                Block/Lot {formatBlockLot(propertyRow.block, propertyRow.lot)} · {relationshipLabel}
              </p>
              <p className="text-xs font-medium text-slate-400 mt-1 break-words">
                Ownership record: {propertyRow.member_name || 'Owner of record'}
              </p>
            </div>
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest whitespace-nowrap ${
                isLinked
                  ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                  : 'bg-amber-50 text-amber-800 border border-amber-200'
              }`}
            >
              {isLinked ? 'Linked' : row.status === 'activated' ? 'Link pending' : 'Access pending'}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const AccMemberToggle = ({ editForm, setEditForm, isPending = false }) => (
  <div className="flex items-center justify-between gap-4 p-4 bg-white rounded-2xl border border-amber-100/30">
    <div>
      <p className="text-sm font-bold text-primary-dark">ACC Committee Member</p>
      <p className="text-xs font-medium text-slate-500 mt-0.5">
        {isPending
          ? 'Applies when this user activates portal access.'
          : 'Shows the ACC badge and review access after next login.'}
      </p>
    </div>
    <button
      type="button"
      onClick={() => setEditForm((prev) => ({ ...prev, is_acc_member: !prev.is_acc_member }))}
      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0 ${editForm.is_acc_member ? 'bg-secondary' : 'bg-slate-200'}`}
      aria-label="Toggle ACC committee membership"
      aria-pressed={editForm.is_acc_member}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${editForm.is_acc_member ? 'translate-x-6' : 'translate-x-1'}`}
      />
    </button>
  </div>
);

const StaffUserCell = ({ account }) => (
  <div className="flex items-center gap-3 min-w-56">
    <div className="w-11 h-11 rounded-full bg-amber-100 text-amber-900 flex items-center justify-center flex-shrink-0 font-extrabold text-sm">
      {getInitials(account.full_name || account.email)}
    </div>
    <div className="min-w-0">
      <p className="text-sm font-bold text-primary-dark truncate">
        {account.full_name || account.email}
      </p>
      <p className="text-xs font-medium text-slate-500 truncate">Non-property account</p>
    </div>
  </div>
);

const UserManagement = () => {
  const {
    user: currentUser,
    effectiveRole,
    startImpersonating,
    isLoading: authLoading,
  } = useAuth();
  const { hoa, isLoading: hoaLoading } = useHOA();
  const hoaId = hoa?.id || currentUser?.hoa_id;
  const isAdmin = effectiveRole === 'admin';

  const [rows, setRows] = useState([]);
  const [staffAccounts, setStaffAccounts] = useState([]);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [inviteModalError, setInviteModalError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openMenu, setOpenMenu] = useState(null);
  const [confirmInvite, setConfirmInvite] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showRosterImport, setShowRosterImport] = useState(false);
  const [createUserError, setCreateUserError] = useState('');
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState({
    full_name: '',
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    is_acc_member: false,
    role: 'member',
  });
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState(() => new Set());
  const [expandedAccountKeys, setExpandedAccountKeys] = useState(() => new Set());
  useModalDismiss({ isOpen: Boolean(editingRow), onClose: () => setEditingRow(null) });

  const loadUsers = useCallback(async () => {
    if (authLoading || hoaLoading) {
      setLoading(true);
      return;
    }

    if (!hoaId) {
      setLoading(false);
      setError('Portal session is not ready. Please refresh or sign in again.');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const propertyColumns = `
          id,
          situs_address,
          block,
          lot,
          phase,
          member_name,
          owner_name_legal,
          email,
          phone,
          membership_status,
          member_invitations(
            id,
            property_id,
            link_type,
            email,
            status,
            invited_at,
            activated_at,
            auth_user_id,
            member_name,
            is_acc_member,
            created_at
          )
        `;
      const { data: properties, error: propertiesError } = await withSupabaseQueryTimeout(
        () =>
          supabase
            .from('properties')
            .select(propertyColumns)
            .eq('hoa_id', hoaId)
            .eq('membership_status', 'active'),
        'Loading HOA users',
        30000
      );

      if (propertiesError) throw propertiesError;

      setPropertyOptions(
        [...(properties || [])].sort(sortProperties).map((property) => ({
          id: property.id,
          label: `${property.situs_address || 'Address unavailable'} | ${formatBlockLot(property.block, property.lot)}`,
        }))
      );

      const { data: nonPropertyInvitations, error: nonPropertyInvitationsError } =
        await withSupabaseQueryTimeout(
          () =>
            supabase
              .from('member_invitations')
              .select(
                'id, property_id, link_type, email, status, invited_at, activated_at, auth_user_id, member_name, is_acc_member, created_at'
              )
              .eq('hoa_id', hoaId)
              .is('property_id', null)
              .order('created_at', { ascending: true }),
          'Loading staff invitations',
          30000
        );
      if (nonPropertyInvitationsError) throw nonPropertyInvitationsError;

      const propertyInvitations = (properties || []).flatMap(
        (property) => property.member_invitations || []
      );
      const allInvitations = [...propertyInvitations, ...(nonPropertyInvitations || [])];
      const profileIds = [
        ...new Set(allInvitations.map((invitation) => invitation.auth_user_id).filter(Boolean)),
      ];

      let profiles = [];
      if (profileIds.length > 0) {
        const { data, error: profilesError } = await withSupabaseQueryTimeout(
          () =>
            supabase
              .from('user_profiles')
              .select(
                'id, email, full_name, first_name, last_name, phone, is_acc_member, is_active, hoa_id'
              )
              .eq('hoa_id', hoaId)
              .in('id', profileIds),
          'Loading invited user profiles',
          30000
        );
        if (profilesError) throw profilesError;
        profiles = data || [];
      }

      const profilesById = new Map(profiles.map((profile) => [profile.id, profile]));
      const allProfileIds = [...new Set(profileIds)];

      const { data: activeProfiles, error: activeProfilesError } = await withSupabaseQueryTimeout(
        () =>
          supabase
            .from('user_profiles')
            .select(
              'id, email, full_name, first_name, last_name, phone, is_acc_member, is_active, hoa_id'
            )
            .eq('hoa_id', hoaId)
            .eq('is_active', true)
            .order('email', { ascending: true }),
        'Loading active portal accounts',
        30000
      );

      if (activeProfilesError) throw activeProfilesError;
      (activeProfiles || []).forEach((profile) => {
        if (!profilesById.has(profile.id)) profilesById.set(profile.id, profile);
        allProfileIds.push(profile.id);
      });

      const uniqueProfileIds = [...new Set(allProfileIds)];
      const lastLoginsByUser = new Map();

      if (isAdmin && uniqueProfileIds.length > 0) {
        try {
          // Same "last visit" as the Activity Log People view: the latest
          // genuine portal login from admin_activity_roster.
          const roster = await getPeopleRoster({ hoaId, includeArchived: true, includeExcluded: true });
          roster.forEach((person) => {
            if (person?.user_id && person?.last_login_at) {
              lastLoginsByUser.set(person.user_id, person.last_login_at);
            }
          });
        } catch (lastLoginError) {
          console.warn(
            'Last login dates could not be loaded:',
            lastLoginError?.message || lastLoginError
          );
        }
      }

      const rolesByUser = new Map();
      if (uniqueProfileIds.length > 0) {
        const { data: rolesData, error: rolesError } = await withSupabaseQueryTimeout(
          () =>
            supabase
              .from('user_roles')
              .select('user_id, roles(name)')
              .in('user_id', uniqueProfileIds)
              .eq('is_active', true),
          'Loading portal account roles',
          30000
        );
        if (rolesError) throw rolesError;
        (rolesData || []).forEach((roleRow) => {
          if (!rolesByUser.has(roleRow.user_id)) {
            rolesByUser.set(roleRow.user_id, roleRow.roles?.name || 'member');
          }
        });
      }

      let activeOccupantLinks = [];
      if (uniqueProfileIds.length > 0) {
        const { data: occupantLinks, error: occupantLinksError } = await withSupabaseQueryTimeout(
          () =>
            supabase
              .from('property_occupants')
              .select('user_id, property_id, is_active')
              .eq('hoa_id', hoaId)
              .eq('is_active', true)
              .in('user_id', uniqueProfileIds),
          'Verifying portal property links',
          30000
        );
        if (occupantLinksError) throw occupantLinksError;
        activeOccupantLinks = occupantLinks || [];
      }

      const invitationRows = (properties || [])
        .flatMap((property) =>
          (property.member_invitations || []).map((invitation) => ({ property, invitation }))
        )
        .sort(sortInvitationRows);

      const ungroupedMemberRows = invitationRows.map(({ property, invitation }, index) => {
        const profile = invitation?.auth_user_id ? profilesById.get(invitation.auth_user_id) : null;
        const status = normalizeStatus(invitation, profile);
        const portalRole = profile ? rolesByUser.get(profile.id) || 'member' : null;
        const displayName =
          status === 'activated'
            ? profile?.full_name ||
              invitation?.member_name ||
              property.member_name ||
              property.owner_name_legal
            : invitation?.member_name || property.member_name || property.owner_name_legal;
        const isCoOccupant = index > 0 && invitationRows[index - 1].property.id === property.id;

        return {
          rowKey: invitation.id,
          property_id: property.id,
          situs_address: property.situs_address,
          block: property.block,
          lot: property.lot,
          member_name: property.member_name || property.owner_name_legal,
          roster_email: property.email,
          roster_phone: property.phone,
          invitation,
          invitation_id: invitation?.id || null,
          link_type: invitation?.link_type || 'owner',
          invitation_email: invitation?.email || '',
          status,
          invited_at: invitation?.invited_at || null,
          activated_at: invitation?.activated_at || null,
          auth_user_id: invitation?.auth_user_id || null,
          last_login_at: invitation?.auth_user_id
            ? lastLoginsByUser.get(invitation.auth_user_id) || null
            : null,
          profile,
          portal_full_name: profile?.full_name || '',
          portal_first_name: profile?.first_name || '',
          portal_last_name: profile?.last_name || '',
          portal_role: portalRole,
          portal_phone: profile?.phone || '',
          portal_is_active: profile?.is_active ?? false,
          is_acc_member: profile?.is_acc_member || invitation?.is_acc_member || false,
          isCoOccupant,
          displayName,
        };
      });
      const mainRows = groupMemberInvitationRows(ungroupedMemberRows, activeOccupantLinks);

      const { data: invitationLinks, error: invitationLinksError } = await withSupabaseQueryTimeout(
        () => supabase.from('member_invitations').select('auth_user_id').eq('hoa_id', hoaId),
        'Loading account property links',
        30000
      );
      if (invitationLinksError) throw invitationLinksError;
      const linkedProfileIds = new Set(
        [
          ...(invitationLinks || []).map((invitation) => invitation.auth_user_id),
          ...activeOccupantLinks.map((link) => link.user_id),
        ].filter(Boolean)
      );

      const invitedStaffRows = (nonPropertyInvitations || []).map((invitation) => {
        const profile = invitation.auth_user_id ? profilesById.get(invitation.auth_user_id) : null;
        const portalRole = profile ? rolesByUser.get(profile.id) || 'member' : null;
        const status = normalizeStatus(invitation, profile);
        const displayName =
          profile?.full_name || invitation.member_name || invitation.email || 'Unnamed';

        return {
          ...(profile || {}),
          id: profile?.id || invitation.auth_user_id || invitation.id,
          rowKey: `staff-invite-${invitation.id}`,
          type: 'staff',
          invitation,
          invitation_id: invitation.id,
          invitation_email: invitation.email || '',
          auth_user_id: invitation.auth_user_id || null,
          last_login_at: invitation.auth_user_id
            ? lastLoginsByUser.get(invitation.auth_user_id) || null
            : null,
          property_id: null,
          link_type: null,
          status,
          displayName,
          portal_full_name: profile?.full_name || invitation.member_name || '',
          portal_first_name: profile?.first_name || '',
          portal_last_name: profile?.last_name || '',
          portal_role: portalRole,
          portal_phone: profile?.phone || '',
          is_acc_member: profile?.is_acc_member || invitation.is_acc_member || false,
          profile,
          role: portalRole,
          email: profile?.email || invitation.email || '',
          phone: profile?.phone || '',
        };
      });

      const standaloneStaffRows = (activeProfiles || [])
        .filter((profile) => !linkedProfileIds.has(profile.id))
        .map((profile) => ({
          ...profile,
          rowKey: `staff-${profile.id}`,
          type: 'staff',
          status: profile.email ? 'activated' : 'no_email',
          displayName: profile.full_name || profile.email || 'Unnamed',
          portal_full_name: profile.full_name || '',
          portal_first_name: profile.first_name || '',
          portal_last_name: profile.last_name || '',
          portal_role: rolesByUser.get(profile.id) || 'member',
          portal_phone: profile.phone || '',
          last_login_at: lastLoginsByUser.get(profile.id) || null,
          profile,
          role: rolesByUser.get(profile.id) || 'member',
        }));

      const staffRows = [...invitedStaffRows, ...standaloneStaffRows].sort((a, b) => {
        const roleDiff = String(a.role || '').localeCompare(String(b.role || ''));
        if (roleDiff !== 0) return roleDiff;
        return String(a.email || '').localeCompare(String(b.email || ''));
      });

      setRows(mainRows);
      setStaffAccounts(staffRows);
    } catch (err) {
      setError(`Failed to load users: ${err.message || 'unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, [authLoading, hoaLoading, hoaId, isAdmin]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const stats = useMemo(() => {
    const activated = rows.filter(
      (row) => row.status === 'activated' && row.portal_is_active
    ).length;
    const invited = rows.filter((row) => row.status === 'invitation_sent').length;
    const ready = rows.filter((row) => row.status === 'not_invited').length;
    const missing = rows.filter((row) => row.status === 'no_email').length;
    const attention = rows.filter((row) => row.status === 'attention').length;
    const uniqueProperties = new Set(
      rows
        .flatMap((row) => (row.propertyRows || [row]).map((property) => property.property_id))
        .filter(Boolean)
    ).size;
    return {
      total: uniqueProperties,
      portalAccounts: rows.length,
      ready,
      activated,
      invited,
      missing,
      attention,
      staff: staffAccounts.length,
    };
  }, [rows, staffAccounts.length]);

  const filteredRows = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== 'all' && row.status !== statusFilter) return false;
      if (!query) return true;
      const searchable = [...(row.searchValues || []), row.displayName, row.invitation_email]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return searchable.includes(query);
    });
  }, [rows, searchTerm, statusFilter]);

  // Staff & unlinked accounts are shown in their own section; search must reach them too
  // so admin can find any user by name/email regardless of property linkage.
  const filteredStaffAccounts = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return staffAccounts;
    return staffAccounts.filter((account) => {
      const searchable = [
        account.displayName,
        account.email,
        account.portal_full_name,
        account.portal_first_name,
        account.portal_last_name,
        account.portal_role,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return searchable.includes(query);
    });
  }, [staffAccounts, searchTerm]);

  const selectableRows = useMemo(
    () => [...rows, ...staffAccounts].filter(canSendInvite),
    [rows, staffAccounts]
  );

  const selectedRows = useMemo(() => {
    const seenEmails = new Set();
    return selectableRows
      .filter((row) => selectedRowKeys.has(row.rowKey))
      .filter((row) => {
        const email = normalizeInviteEmail(getInviteEmail(row));
        if (!email || seenEmails.has(email)) return false;
        seenEmails.add(email);
        return true;
      });
  }, [selectableRows, selectedRowKeys]);

  const selectableFilteredRows = useMemo(() => filteredRows.filter(canSendInvite), [filteredRows]);

  const allFilteredSelected =
    selectableFilteredRows.length > 0 &&
    selectableFilteredRows.every((row) => selectedRowKeys.has(row.rowKey));
  const someFilteredSelected = selectableFilteredRows.some((row) =>
    selectedRowKeys.has(row.rowKey)
  );

  useEffect(() => {
    setSelectedRowKeys((current) => {
      const validKeys = new Set(selectableRows.map((row) => row.rowKey));
      const next = new Set([...current].filter((key) => validKeys.has(key)));
      if (next.size === current.size) return current;
      return next;
    });
  }, [selectableRows]);

  const toggleRowSelection = (row) => {
    if (!canSendInvite(row)) return;
    setSelectedRowKeys((current) => {
      const next = new Set(current);
      if (next.has(row.rowKey)) {
        next.delete(row.rowKey);
      } else {
        next.add(row.rowKey);
      }
      return next;
    });
  };

  const toggleAccountExpanded = (rowKey) => {
    setExpandedAccountKeys((current) => {
      const next = new Set(current);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  const toggleFilteredSelection = () => {
    setSelectedRowKeys((current) => {
      const next = new Set(current);
      if (allFilteredSelected) {
        selectableFilteredRows.forEach((row) => next.delete(row.rowKey));
      } else {
        selectableFilteredRows.forEach((row) => next.add(row.rowKey));
      }
      return next;
    });
  };

  const beginBulkInvite = () => {
    if (selectedRows.length === 0) return;
    setInviteModalError('');
    setConfirmInvite({
      invites: selectedRows.map((row) => ({ row, email: getInviteEmail(row) })),
      isBulk: true,
    });
  };

  const openEdit = (row, type = 'member') => {
    setEditingRow({ ...row, type });
    setEditForm({
      full_name: row.portal_full_name || row.displayName || row.full_name || '',
      first_name: row.portal_first_name || row.first_name || row.profile?.first_name || '',
      last_name: row.portal_last_name || row.last_name || row.profile?.last_name || '',
      phone: row.portal_phone || row.roster_phone || row.phone || '',
      email: row.invitation_email || row.roster_email || row.email || '',
      is_acc_member: row.is_acc_member || false,
      role: row.portal_role || row.role || 'member',
    });
  };

  const saveEdit = async (event) => {
    event.preventDefault();
    if (!editingRow) return;
    setActionLoading(`edit-${editingRow.rowKey || editingRow.id}`);
    setError('');
    setSuccessMessage('');

    try {
      const cleanFirstName = editForm.first_name.trim();
      const cleanLastName = editForm.last_name.trim();
      const cleanFullName =
        editForm.full_name.trim() || [cleanFirstName, cleanLastName].filter(Boolean).join(' ');

      if (editingRow.type === 'staff' && editingRow.profile?.id) {
        const { error: profileError } = await supabase
          .from('user_profiles')
          .update({
            full_name: cleanFullName,
            first_name: cleanFirstName || null,
            last_name: cleanLastName || null,
            phone: editForm.phone,
            is_acc_member: editForm.is_acc_member,
          })
          .eq('id', editingRow.profile.id)
          .eq('hoa_id', hoaId);
        if (profileError) throw profileError;
      } else if (editingRow.status === 'activated' && editingRow.profile?.id) {
        const { error: profileError } = await supabase
          .from('user_profiles')
          .update({
            full_name: cleanFullName,
            first_name: cleanFirstName || null,
            last_name: cleanLastName || null,
            phone: editForm.phone,
            is_acc_member: editForm.is_acc_member,
          })
          .eq('id', editingRow.profile.id)
          .eq('hoa_id', hoaId);
        if (profileError) throw profileError;
      } else if (editingRow.invitation_id) {
        const cleanedEmail = editForm.email.trim() || null;
        let invitationUpdate = supabase
          .from('member_invitations')
          .update({
            email: cleanedEmail,
            member_name: cleanFullName || editingRow.member_name || null,
            status: cleanedEmail ? 'not_invited' : 'no_email',
            is_acc_member: editForm.is_acc_member,
          })
          .eq('hoa_id', hoaId);
        invitationUpdate = editingRow.invitation_ids?.length
          ? invitationUpdate.in('id', editingRow.invitation_ids)
          : invitationUpdate.eq('id', editingRow.invitation_id);
        const { error: invitationError } = await invitationUpdate;
        if (invitationError) throw invitationError;
      }

      // Role change (admin only, activated users only, never self).
      const targetAuthId = editingRow.profile?.id || null;
      const originalRole = editingRow.portal_role || editingRow.role || null;
      const requestedRole = editForm.role || null;
      const roleChanged =
        isAdmin &&
        targetAuthId &&
        targetAuthId !== currentUser?.id &&
        requestedRole &&
        requestedRole !== originalRole;

      if (roleChanged) {
        try {
          const { error: roleErr } = await supabase.rpc('set_user_role', {
            target_user_id: targetAuthId,
            new_role_name: requestedRole,
          });
          if (roleErr) throw roleErr;
          logEvent('role_changed', {
            target_user_id: targetAuthId,
            old_role: originalRole,
            new_role: requestedRole,
          });
        } catch (roleException) {
          console.error('Role update failed', roleException);
          setError(
            `Profile saved, but role change failed: ${roleException?.message || 'unknown error'}`
          );
          setActionLoading(null);
          await loadUsers();
          return;
        }
      }

      logEvent('profile_updated', {
        target_user_id: editingRow.profile?.id || editingRow.id || null,
        invitation_id: editingRow.invitation_id || null,
      });
      setSuccessMessage('User details updated.');
      setEditingRow(null);
      await loadUsers();
    } catch (err) {
      setError(`Failed to save changes: ${err.message || 'unknown error'}`);
    } finally {
      setActionLoading(null);
    }
  };

  const beginDelete = () => {
    if (!isAdmin || !editingRow) return;
    const targetUserId = getDeleteTargetUserId(editingRow);
    if (targetUserId && targetUserId === currentUser?.id) {
      setError('You cannot delete your own administrator account.');
      return;
    }

    setDeleteError('');
    setConfirmDelete(editingRow);
    setEditingRow(null);
  };

  const deletePortalUser = async () => {
    if (!isAdmin || !confirmDelete) return;
    const displayName = getDeleteDisplayName(confirmDelete);
    setActionLoading('delete-user');
    setDeleteError('');
    setError('');
    setSuccessMessage('');

    try {
      await revokePortalUser({ row: confirmDelete, hoaId });
      setSelectedRowKeys((current) => {
        const next = new Set(current);
        next.delete(confirmDelete.rowKey);
        return next;
      });
      setConfirmDelete(null);
      setSuccessMessage(
        `${displayName} no longer has portal access. Historical activity and messages were preserved.`
      );
      await loadUsers();
    } catch (err) {
      setDeleteError(`Failed to delete user: ${err.message || 'unknown error'}`);
    } finally {
      setActionLoading(null);
    }
  };

  const beginInvite = (row, isResend = false) => {
    const email = getInviteEmail(row);
    if (!email) {
      setError('Add an email before sending an invitation.');
      return;
    }
    setInviteModalError('');
    setConfirmInvite({ invites: [{ row, email }], isResend });
  };

  const sendInviteRequest = async ({ row, email }) => {
    const data = await sendInvitation({ row, email, hoaId });
    logEvent('user_created', { invited_email: email, property_id: row.property_id || null });
    return data;
  };

  const sendInvite = async () => {
    if (!confirmInvite?.invites?.length) return;
    const invites = confirmInvite.invites;
    const isBulk = invites.length > 1;
    setActionLoading(isBulk ? 'invite-bulk' : `invite-${invites[0].row.rowKey}`);
    setError('');
    setInviteModalError('');
    setSuccessMessage('');

    try {
      const failures = [];
      const results = [];
      for (const invite of invites) {
        try {
          const result = await sendInviteRequest(invite);
          results.push(result);
        } catch (err) {
          failures.push({
            email: invite.email,
            message: err.message || 'unknown error',
          });
        }
      }

      if (failures.length > 0) {
        const firstFailure = failures[0];
        if (!isBulk) {
          throw new Error(`${firstFailure.email}: ${firstFailure.message}`);
        }
        throw new Error(
          `${failures.length} of ${invites.length} failed. ${firstFailure.email}: ${firstFailure.message}`
        );
      }

      const resetCount = results.filter((result) => result?.reset_performed).length;
      setSuccessMessage(
        isBulk
          ? resetCount > 0
            ? `${invites.length} invitations sent. ${resetCount} existing account${resetCount === 1 ? '' : 's'} reset first.`
            : `${invites.length} invitations sent.`
          : resetCount > 0
            ? `Existing access reset and invitation sent to ${invites[0].email}.`
            : `Invitation sent to ${invites[0].email}.`
      );
      setConfirmInvite(null);
      setSelectedRowKeys(new Set());
      await loadUsers();
    } catch (err) {
      const message = `Failed to send invitation: ${err.message || 'unknown error'}`;
      setInviteModalError(message);
    } finally {
      setActionLoading(null);
    }
  };

  const createUser = async (form) => {
    if (!isAdmin) {
      setCreateUserError('Only administrators can create portal accounts.');
      return;
    }

    const fullName = [form.first_name, form.last_name].filter(Boolean).join(' ').trim();
    setActionLoading('create-user');
    setCreateUserError('');
    setError('');
    setSuccessMessage('');

    try {
      const newAccount = {
        rowKey: `new-${form.email}`,
        displayName: fullName || form.email,
        full_name: fullName,
        first_name: form.first_name,
        last_name: form.last_name,
        portal_first_name: form.first_name,
        portal_last_name: form.last_name,
        phone: form.phone,
        portal_phone: form.phone,
        email: form.email,
        invitation_email: form.email,
        role: form.role,
        portal_role: form.role,
        property_id: form.property_id || null,
        link_type: form.link_type,
        is_acc_member: form.is_acc_member,
        type: form.account_type === 'property' ? 'member' : 'staff',
        status: 'not_invited',
      };

      await sendInvitation({ row: newAccount, email: form.email, hoaId });
      logEvent('user_created', {
        invited_email: form.email,
        assigned_role: form.role,
        property_id: form.property_id || null,
      });
      setShowCreateUser(false);
      setSuccessMessage(`Invitation sent to ${form.email}.`);
      await loadUsers();
    } catch (err) {
      setCreateUserError(`Failed to create user: ${err.message || 'unknown error'}`);
    } finally {
      setActionLoading(null);
    }
  };

  const viewAs = (row) => {
    const profile = row.profile || row;
    startImpersonating({
      id: profile.id,
      full_name: profile.full_name || row.displayName || row.email,
      email: profile.email || row.email,
      role: row.portal_role || row.role || 'member',
      permission_level:
        row.portal_role === 'admin' || row.role === 'admin'
          ? 100
          : row.portal_role === 'board' || row.role === 'board'
            ? 50
            : row.portal_role === 'management' || row.role === 'management'
              ? 30
              : 10,
    });
  };

  const memberActions = (row) => {
    if (row.status === 'no_email') {
      return [{ label: 'Edit', icon: 'edit', onClick: () => openEdit(row) }];
    }
    if (row.status === 'not_invited') {
      return [
        { label: 'Send Invite', icon: 'send', onClick: () => beginInvite(row, false) },
        { label: 'Edit', icon: 'edit', onClick: () => openEdit(row) },
      ];
    }
    if (row.status === 'invitation_sent') {
      return [
        { label: 'Resend Invite', icon: 'send', onClick: () => beginInvite(row, true) },
        { label: 'Edit', icon: 'edit', onClick: () => openEdit(row) },
      ];
    }
    return [
      { label: 'Resend Invite', icon: 'send', onClick: () => beginInvite(row, true) },
      ...(isAdmin && row.profile?.id !== currentUser?.id
        ? [{ label: 'View As', icon: 'visibility', onClick: () => viewAs(row) }]
        : []),
      { label: 'Edit Profile', icon: 'edit', onClick: () => openEdit(row) },
    ];
  };

  const staffActions = (account) => [
    ...(canSendInvite(account)
      ? [{ label: 'Resend Invite', icon: 'send', onClick: () => beginInvite(account, true) }]
      : []),
    { label: 'Edit Profile', icon: 'edit', onClick: () => openEdit(account, 'staff') },
    ...(isAdmin && account.profile?.id && account.profile.id !== currentUser?.id
      ? [{ label: 'View As', icon: 'visibility', onClick: () => viewAs(account) }]
      : []),
  ];

  const subtitle = `${stats.total} properties · ${stats.portalAccounts} portal accounts · ${stats.activated} activated · ${stats.invited} invited · ${stats.ready} ready to invite · ${stats.missing} missing email${stats.attention ? ` · ${stats.attention} need review` : ''} · ${stats.staff} other accounts`;

  return (
    <div className="space-y-8">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm font-medium flex items-center justify-between gap-4">
          <span>{error}</span>
          <button
            type="button"
            onClick={loadUsers}
            className="border border-red-200 px-4 py-2 rounded-full font-bold hover:bg-red-100 transition-all"
          >
            Retry
          </button>
        </div>
      )}
      {successMessage && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-2xl text-sm font-medium flex items-center justify-between gap-4">
          <span>{successMessage}</span>
          <button
            type="button"
            onClick={() => setSuccessMessage('')}
            className="w-9 h-9 rounded-full hover:bg-emerald-100 flex items-center justify-center"
          >
            <MaterialIcon name="close" size={18} />
          </button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-primary-dark">Users</h2>
          <p className="text-sm font-medium text-slate-500 mt-1">{subtitle}</p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={() => setShowRosterImport(true)}
              className="border border-secondary text-secondary px-6 py-3 rounded-full font-bold text-sm hover:bg-secondary hover:text-white transition-all flex items-center justify-center gap-2"
              data-testid="open-roster-import"
            >
              <MaterialIcon name="upload_file" size={19} />
              Import spreadsheet
            </button>
            <button
              type="button"
              onClick={() => {
                setCreateUserError('');
                setShowCreateUser(true);
              }}
              className="bg-secondary text-white px-6 py-3 rounded-full font-bold text-sm hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-2"
            >
              <MaterialIcon name="person_add" size={19} />
              Add User
            </button>
          </div>
        )}
      </div>

      <section className="bg-section-bg p-4 sm:p-6 lg:p-8 rounded-[2.5rem]">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
          <StatCard
            icon="group"
            label="Total Members"
            value={loading ? '-' : stats.total}
            testId="stat-total"
          />
          <StatCard
            icon="mail"
            label="Ready to Invite"
            value={loading ? '-' : stats.ready}
            accent="text-secondary"
            testId="stat-ready"
          />
          <StatCard
            icon="check_circle"
            label="Activated"
            value={loading ? '-' : stats.activated}
            accent="text-emerald-700"
            testId="stat-activated"
          />
          <StatCard
            icon="warning"
            label="Missing Email"
            value={loading ? '-' : stats.missing}
            accent="text-amber-600"
            testId="stat-no-email"
          />
        </div>
      </section>

      <section className="bg-section-bg p-4 sm:p-6 lg:p-8 rounded-[2.5rem] space-y-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="relative flex-1">
            <MaterialIcon
              name="search"
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Search by name, address, or email..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-amber-100/30 focus:border-secondary focus:ring-2 focus:ring-secondary/10 rounded-full text-primary-dark placeholder-slate-400 text-sm font-medium outline-none transition-all"
            />
          </div>
          <div className="flex flex-col gap-3 lg:w-72">
            <AnimatePresence initial={false}>
              {selectedRows.length > 0 && (
                <motion.button
                  type="button"
                  onClick={beginBulkInvite}
                  disabled={!!actionLoading}
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  className="bg-secondary text-white px-6 py-3 rounded-full font-bold text-sm hover:brightness-110 active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <MaterialIcon name="send" size={18} />
                  Send Invitation ({selectedRows.length})
                </motion.button>
              )}
            </AnimatePresence>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="px-6 py-3 bg-white border border-amber-100/30 focus:border-secondary rounded-full text-primary-dark text-sm font-medium outline-none transition-all cursor-pointer"
            >
              <option value="all">All statuses</option>
              <option value="activated">Activated</option>
              <option value="invitation_sent">Invited</option>
              <option value="not_invited">Not invited</option>
              <option value="no_email">No email</option>
              <option value="attention">Needs review</option>
            </select>
          </div>
        </div>

        {filteredRows.length === 0 && !loading ? (
          <EmptyState
            title="No members match your search"
            description="Try a different name, address, email, or status filter."
          />
        ) : (
          <div className="bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30 overflow-hidden">
            <div className="hidden lg:block">
              <table className="w-full table-fixed">
                <colgroup>
                  {(isAdmin
                    ? ['w-[4%]', 'w-[20%]', 'w-[10%]', 'w-[15%]', 'w-[8%]', 'w-[17%]', 'w-[10%]', 'w-[4%]', 'w-[8%]', 'w-[4%]']
                    : ['w-[4%]', 'w-[22%]', 'w-[11%]', 'w-[17%]', 'w-[9%]', 'w-[18%]', 'w-[11%]', 'w-[4%]', 'w-[4%]']
                  ).map((width, index) => (
                    <col key={`${width}-${index}`} className={width} />
                  ))}
                </colgroup>
                <thead className="bg-section-bg">
                  <tr>
                    <th className="text-left px-3 py-3">
                      <input
                        type="checkbox"
                        checked={allFilteredSelected}
                        ref={(input) => {
                          if (input)
                            input.indeterminate = !allFilteredSelected && someFilteredSelected;
                        }}
                        onChange={toggleFilteredSelection}
                        disabled={selectableFilteredRows.length === 0 || !!actionLoading}
                        aria-label="Select all invite-eligible members"
                        className="w-5 h-5 rounded border-amber-200 text-secondary focus:ring-secondary/20 disabled:opacity-40"
                      />
                    </th>
                    {[
                      'User',
                      'Role',
                      'Address',
                      'Block/Lot',
                      'Contact',
                      'Status',
                      'ACC',
                      ...(isAdmin ? ['Last Login'] : []),
                      'Actions',
                    ].map((heading) => (
                      <th
                        key={heading}
                        className="text-left px-3 py-3 text-xs font-bold uppercase tracking-widest text-slate-500"
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-amber-100/40" data-testid="users-rows">
                  {loading ? (
                    <SkeletonRows colSpan={isAdmin ? 10 : 9} />
                  ) : (
                    filteredRows.map((row, index) => (
                      <React.Fragment key={row.rowKey}>
                        <motion.tr
                          data-testid="users-row"
                          data-property-count={row.property_count}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: Math.min(index * 0.01, 0.2) }}
                          className={`hover:bg-section-bg/50 transition-colors ${row.has_link_mismatch ? 'border-l-4 border-rose-300' : ''}`}
                        >
                          <td className="px-3 py-3">
                            <input
                              type="checkbox"
                              checked={selectedRowKeys.has(row.rowKey)}
                              onChange={() => toggleRowSelection(row)}
                              disabled={!canSendInvite(row) || !!actionLoading}
                              aria-label={`Select ${row.displayName || row.member_name || getInviteEmail(row) || 'member'}`}
                              className="w-5 h-5 rounded border-amber-200 text-secondary focus:ring-secondary/20 disabled:opacity-40"
                            />
                          </td>
                          <td className="px-3 py-3">
                            <UserCell row={row} />
                          </td>
                          <td className="px-3 py-3">
                            <RoleBadge role={row.portal_role} />
                          </td>
                          <td className="px-3 py-3 text-xs font-semibold break-words text-primary-dark">
                            <p>{row.situs_address || '-'}</p>
                            {row.property_count > 1 && (
                              <button
                                type="button"
                                onClick={() => toggleAccountExpanded(row.rowKey)}
                                className="mt-2 inline-flex items-center gap-1 text-secondary font-bold rounded-full hover:bg-section-bg px-2 py-1 -ml-2 transition-all"
                                aria-expanded={expandedAccountKeys.has(row.rowKey)}
                              >
                                <MaterialIcon
                                  name={
                                    expandedAccountKeys.has(row.rowKey)
                                      ? 'expand_less'
                                      : 'expand_more'
                                  }
                                  size={17}
                                />
                                {expandedAccountKeys.has(row.rowKey)
                                  ? 'Hide properties'
                                  : `Show all ${row.property_count}`}
                              </button>
                            )}
                          </td>
                          <td className="px-3 py-3 text-xs font-semibold text-slate-500">
                            {row.property_count > 1
                              ? `${row.property_count} properties`
                              : formatBlockLot(row.block, row.lot)}
                          </td>
                          <td className="px-3 py-3 text-xs min-w-0">
                            <div className="font-bold text-primary-dark break-words">
                              {row.invitation_email || row.roster_email || (
                                <span className="text-slate-400">No email</span>
                              )}
                            </div>
                            <div className="text-xs font-medium text-slate-500 mt-0.5">
                              {row.portal_phone || row.roster_phone || '-'}
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <StatusBadge status={row.status} />
                          </td>
                          <td className="px-3 py-3">
                            <ACCBadge show={row.is_acc_member} />
                          </td>
                          {isAdmin && (
                            <td className="px-3 py-3">
                              <LastLoginValue value={row.last_login_at} />
                            </td>
                          )}
                          <td className="px-3 py-3">
                            <ActionMenu
                              rowKey={row.rowKey}
                              openMenu={openMenu}
                              setOpenMenu={setOpenMenu}
                              actions={memberActions(row)}
                              disabled={!!actionLoading}
                            />
                          </td>
                        </motion.tr>
                        {expandedAccountKeys.has(row.rowKey) && row.property_count > 1 && (
                          <tr data-testid="user-property-links">
                            <td colSpan={isAdmin ? 10 : 9} className="bg-section-bg/70 px-6 py-5">
                              <div className="mb-3 flex items-center justify-between gap-4">
                                <div>
                                  <p className="text-sm font-bold text-primary-dark">
                                    Property access for {row.displayName}
                                  </p>
                                  <p className="text-xs font-medium text-slate-500 mt-0.5">
                                    One portal account · {row.property_count} property relationships
                                  </p>
                                </div>
                                {row.status === 'attention' && (
                                  <span className="text-xs font-bold text-rose-700">
                                    {row.linked_property_count} of {row.property_count} linked
                                  </span>
                                )}
                              </div>
                              <PropertyRelationshipList row={row} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="lg:hidden divide-y divide-amber-100/40">
              {loading ? (
                <div className="p-5 space-y-4 animate-pulse">
                  {[0, 1, 2, 3].map((item) => (
                    <div key={item} className="h-24 bg-section-bg rounded-2xl" />
                  ))}
                </div>
              ) : (
                filteredRows.map((row) => (
                  <div
                    key={row.rowKey}
                    data-testid="users-card"
                    data-property-count={row.property_count}
                    className={`p-5 space-y-4 ${row.has_link_mismatch ? 'border-l-4 border-rose-300' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 min-w-0">
                        <input
                          type="checkbox"
                          checked={selectedRowKeys.has(row.rowKey)}
                          onChange={() => toggleRowSelection(row)}
                          disabled={!canSendInvite(row) || !!actionLoading}
                          aria-label={`Select ${row.displayName || row.member_name || getInviteEmail(row) || 'member'}`}
                          className="mt-3 w-5 h-5 rounded border-amber-200 text-secondary focus:ring-secondary/20 disabled:opacity-40 flex-shrink-0"
                        />
                        <UserCell row={row} />
                      </div>
                      <ActionMenu
                        rowKey={row.rowKey}
                        openMenu={openMenu}
                        setOpenMenu={setOpenMenu}
                        actions={memberActions(row)}
                        disabled={!!actionLoading}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                          Role
                        </p>
                        <div className="mt-1">
                          <RoleBadge role={row.portal_role} />
                        </div>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                          Status
                        </p>
                        <div className="mt-1">
                          <StatusBadge status={row.status} />
                        </div>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                          Block/Lot
                        </p>
                        <p className="font-bold mt-1 text-primary-dark">
                          {row.property_count > 1
                            ? `${row.property_count} properties`
                            : formatBlockLot(row.block, row.lot)}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                          ACC
                        </p>
                        <div className="mt-1">
                          <ACCBadge show={row.is_acc_member} />
                        </div>
                      </div>
                      {isAdmin && (
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                            Last Login
                          </p>
                          <div className="mt-1">
                            <LastLoginValue value={row.last_login_at} />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="rounded-2xl bg-section-bg p-4 border border-amber-100/30">
                      <p className="text-xs font-bold text-primary-dark break-words">
                        {row.invitation_email || row.roster_email || 'No email'}
                      </p>
                      <p className="text-xs font-medium text-slate-500 mt-1">
                        {row.portal_phone || row.roster_phone || '-'}
                      </p>
                    </div>
                    {row.property_count > 1 && (
                      <div>
                        <button
                          type="button"
                          onClick={() => toggleAccountExpanded(row.rowKey)}
                          className="w-full border border-secondary text-secondary px-5 py-2.5 rounded-full font-bold text-sm hover:bg-secondary hover:text-white transition-all flex items-center justify-center gap-2"
                          aria-expanded={expandedAccountKeys.has(row.rowKey)}
                        >
                          <MaterialIcon
                            name={
                              expandedAccountKeys.has(row.rowKey) ? 'expand_less' : 'expand_more'
                            }
                            size={18}
                          />
                          {expandedAccountKeys.has(row.rowKey)
                            ? 'Hide properties'
                            : `Show all ${row.property_count} properties`}
                        </button>
                        {expandedAccountKeys.has(row.rowKey) && (
                          <div className="mt-4 rounded-[2.5rem] bg-section-bg p-4">
                            <PropertyRelationshipList row={row} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </section>

      {staffAccounts.length > 0 && (
        <section className="bg-section-bg rounded-[2.5rem] p-4 sm:p-6 lg:p-8 space-y-6">
          <div>
            <h3 className="text-2xl font-bold tracking-tight text-primary-dark">
              Staff & non-property accounts
            </h3>
            <p className="text-sm font-medium text-slate-500 mt-1">
              {searchTerm.trim()
                ? `${filteredStaffAccounts.length} of ${staffAccounts.length} match your search`
                : `${staffAccounts.length} accounts not linked to a member property`}
            </p>
          </div>

          <div className="bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30 overflow-hidden">
            <div className="hidden lg:block">
              <table className="w-full table-fixed">
                <colgroup>
                  {(isAdmin
                    ? ['w-[5%]', 'w-[21%]', 'w-[10%]', 'w-[20%]', 'w-[11%]', 'w-[10%]', 'w-[6%]', 'w-[11%]', 'w-[6%]']
                    : ['w-[5%]', 'w-[24%]', 'w-[12%]', 'w-[23%]', 'w-[13%]', 'w-[11%]', 'w-[6%]', 'w-[6%]']
                  ).map((width, index) => (
                    <col key={`${width}-${index}`} className={width} />
                  ))}
                </colgroup>
                <thead className="bg-section-bg">
                  <tr>
                    <th className="text-left px-3 py-3">
                      <span className="sr-only">Select</span>
                    </th>
                    {['User', 'Role', 'Email', 'Phone', 'Status', 'ACC', ...(isAdmin ? ['Last Login'] : []), 'Actions'].map(
                      (heading) => (
                        <th
                          key={heading}
                          className="text-left px-3 py-3 text-xs font-bold uppercase tracking-widest text-slate-500"
                        >
                          {heading}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-amber-100/40">
                  {filteredStaffAccounts.map((account) => (
                    <tr key={account.id} className="hover:bg-section-bg/50 transition-colors">
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={selectedRowKeys.has(account.rowKey)}
                          onChange={() => toggleRowSelection(account)}
                          disabled={!canSendInvite(account) || !!actionLoading}
                          aria-label={`Select ${account.full_name || account.email || 'staff account'}`}
                          className="w-5 h-5 rounded border-amber-200 text-secondary focus:ring-secondary/20 disabled:opacity-40"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <StaffUserCell account={account} />
                      </td>
                      <td className="px-3 py-3">
                        <RoleBadge role={account.role} />
                      </td>
                      <td className="px-3 py-3 text-xs font-bold text-primary-dark break-words">
                        {account.email || '-'}
                      </td>
                      <td className="px-3 py-3 text-xs font-medium text-slate-500 break-words">
                        {account.phone || '-'}
                      </td>
                      <td className="px-3 py-3">
                        <StatusBadge status={account.status} />
                      </td>
                      <td className="px-3 py-3">
                        <ACCBadge show={account.is_acc_member} />
                      </td>
                      {isAdmin && (
                        <td className="px-3 py-3">
                          <LastLoginValue value={account.last_login_at} />
                        </td>
                      )}
                      <td className="px-3 py-3">
                        <ActionMenu
                          rowKey={`staff-${account.id}`}
                          openMenu={openMenu}
                          setOpenMenu={setOpenMenu}
                          actions={staffActions(account)}
                          disabled={!!actionLoading}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="lg:hidden divide-y divide-amber-100/40">
              {filteredStaffAccounts.map((account) => (
                <div key={account.id} className="p-5 space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <input
                        type="checkbox"
                        checked={selectedRowKeys.has(account.rowKey)}
                        onChange={() => toggleRowSelection(account)}
                        disabled={!canSendInvite(account) || !!actionLoading}
                        aria-label={`Select ${account.full_name || account.email || 'staff account'}`}
                        className="mt-3 w-5 h-5 rounded border-amber-200 text-secondary focus:ring-secondary/20 disabled:opacity-40 flex-shrink-0"
                      />
                      <StaffUserCell account={account} />
                    </div>
                    <ActionMenu
                      rowKey={`staff-${account.id}`}
                      openMenu={openMenu}
                      setOpenMenu={setOpenMenu}
                      actions={staffActions(account)}
                      disabled={!!actionLoading}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <RoleBadge role={account.role} />
                    </div>
                    <div>
                      <StatusBadge status={account.status} />
                    </div>
                    <div>
                      <ACCBadge show={account.is_acc_member} />
                    </div>
                    {isAdmin && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">
                          Last Login
                        </p>
                        <LastLoginValue value={account.last_login_at} />
                      </div>
                    )}
                  </div>
                  <div className="rounded-2xl bg-section-bg p-4 border border-amber-100/30">
                    <p className="text-xs font-bold text-primary-dark break-words">
                      {account.email || '-'}
                    </p>
                    <p className="text-xs font-medium text-slate-500 mt-1">
                      {account.phone || '-'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <AnimatePresence>
        {showCreateUser && (
          <CreateUserModal
            open={showCreateUser}
            properties={propertyOptions}
            loading={actionLoading === 'create-user'}
            error={createUserError}
            onClose={() => {
              if (actionLoading === 'create-user') return;
              setCreateUserError('');
              setShowCreateUser(false);
            }}
            onSubmit={createUser}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showRosterImport && (
          <RosterImportModal
            open={showRosterImport}
            hoa={{ ...hoa, id: hoaId }}
            onClose={() => setShowRosterImport(false)}
            onImported={() => loadUsers()}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {editingRow && (
          <motion.div
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            role="presentation"
            data-modal-backdrop="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setEditingRow(null)}
          >
            <motion.div
              className="bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30 max-w-lg w-full p-6"
              role="dialog"
              aria-modal="true"
              aria-labelledby="edit-profile-title"
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-secondary/10 rounded-xl flex items-center justify-center">
                    <MaterialIcon name="edit" className="text-secondary" size={22} />
                  </div>
                  <div>
                    <h3 id="edit-profile-title" className="text-lg font-bold text-primary-dark">Edit Profile</h3>
                    <p className="text-sm font-medium text-slate-500">
                      {editingRow.invitation_email || editingRow.email || editingRow.situs_address}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setEditingRow(null)}
                  aria-label="Close edit profile dialog"
                  className="w-10 h-10 rounded-full hover:bg-section-bg flex items-center justify-center"
                >
                  <MaterialIcon name="close" size={20} />
                </button>
              </div>

              <form onSubmit={saveEdit} className="space-y-5">
                <div className="p-5 bg-section-bg rounded-2xl border border-amber-100/30 space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-widest">
                      {editingRow.status === 'activated'
                        ? 'Portal display name'
                        : 'Ownership / invitation name'}
                    </label>
                    <input
                      type="text"
                      value={editForm.full_name}
                      onChange={(event) =>
                        setEditForm((prev) => ({ ...prev, full_name: event.target.value }))
                      }
                      className={inputClass}
                    />
                  </div>
                  {editingRow.status !== 'activated' && editingRow.type !== 'staff' && (
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-widest">
                        Invitation email
                      </label>
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(event) =>
                          setEditForm((prev) => ({ ...prev, email: event.target.value }))
                        }
                        className={inputClass}
                      />
                      {editingRow.property_count > 1 && (
                        <p className="text-xs font-medium text-slate-500 mt-2 leading-relaxed">
                          This email will be used for one portal invitation covering all{' '}
                          {editingRow.property_count} property relationships.
                        </p>
                      )}
                    </div>
                  )}
                  {editingRow.status !== 'activated' && editingRow.type !== 'staff' && (
                    <AccMemberToggle editForm={editForm} setEditForm={setEditForm} isPending />
                  )}
                  {(editingRow.status === 'activated' || editingRow.type === 'staff') && (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-widest">
                            First name
                          </label>
                          <input
                            type="text"
                            value={editForm.first_name}
                            onChange={(event) =>
                              setEditForm((prev) => ({ ...prev, first_name: event.target.value }))
                            }
                            className={inputClass}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-widest">
                            Last name
                          </label>
                          <input
                            type="text"
                            value={editForm.last_name}
                            onChange={(event) =>
                              setEditForm((prev) => ({ ...prev, last_name: event.target.value }))
                            }
                            className={inputClass}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-widest">
                          Phone
                        </label>
                        <input
                          type="tel"
                          value={editForm.phone}
                          onChange={(event) =>
                            setEditForm((prev) => ({ ...prev, phone: event.target.value }))
                          }
                          className={inputClass}
                        />
                      </div>
                      {isAdmin && getDeleteTargetUserId(editingRow) !== currentUser?.id && (
                        <div>
                          <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-widest">
                            Role
                          </label>
                          <select
                            className={inputClass}
                            value={editForm.role || 'member'}
                            onChange={(event) =>
                              setEditForm((prev) => ({ ...prev, role: event.target.value }))
                            }
                          >
                            {ROLE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs font-medium text-slate-500 mt-1.5 leading-relaxed">
                            Changes this user's access level. You cannot change your own role.
                          </p>
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-4 p-4 bg-white rounded-2xl border border-amber-100/30">
                        <div>
                          <p className="text-sm font-bold text-primary-dark">
                            ACC Committee Member
                          </p>
                          <p className="text-xs font-medium text-slate-500 mt-0.5">
                            Shows the ACC badge and review access after next login.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setEditForm((prev) => ({ ...prev, is_acc_member: !prev.is_acc_member }))
                          }
                          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0 ${editForm.is_acc_member ? 'bg-secondary' : 'bg-slate-200'}`}
                          aria-pressed={editForm.is_acc_member}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${editForm.is_acc_member ? 'translate-x-6' : 'translate-x-1'}`}
                          />
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    {isAdmin &&
                      getDeleteTargetUserId(editingRow) !== currentUser?.id &&
                      (getDeleteTargetUserId(editingRow) || editingRow.property_count <= 1) && (
                        <button
                          type="button"
                          onClick={beginDelete}
                          disabled={!!actionLoading}
                          className="border border-red-200 text-red-700 px-5 py-2.5 rounded-full font-bold text-sm hover:bg-red-50 transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2"
                        >
                          <MaterialIcon name="delete_forever" size={18} />
                          Delete User
                        </button>
                      )}
                  </div>
                  <div className="flex items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setEditingRow(null)}
                      className="border border-slate-200 text-slate-700 px-6 py-2.5 rounded-full font-bold text-sm hover:bg-section-bg transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={!!actionLoading}
                      className="bg-secondary text-white px-6 py-2.5 rounded-full font-bold text-sm hover:brightness-110 active:scale-95 transition-all disabled:opacity-50"
                    >
                      {actionLoading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <InviteConfirmModal
        invite={confirmInvite}
        loading={actionLoading?.startsWith('invite-')}
        error={inviteModalError}
        onCancel={() => {
          setInviteModalError('');
          setConfirmInvite(null);
        }}
        onConfirm={sendInvite}
      />

      <ConfirmDialog
        isOpen={!!confirmDelete}
        title="Delete portal user?"
        message={
          confirmDelete
            ? `${getDeleteDisplayName(confirmDelete)} will immediately lose portal access and disappear from active user and member lists. Their activity log, messages, ACC comments, and other historical records will remain.`
            : ''
        }
        confirmLabel="Delete User"
        variant="danger"
        loading={actionLoading === 'delete-user'}
        error={deleteError}
        onCancel={() => {
          if (actionLoading === 'delete-user') return;
          setDeleteError('');
          setConfirmDelete(null);
        }}
        onConfirm={deletePortalUser}
      />
    </div>
  );
};

export default UserManagement;
